import { Typography, styled } from '@mui/material';
import { Stack } from '@mui/system';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useAuthContext } from 'src/auth/useAuthContext';
import { Loading } from 'src/components/Loading/Loading';
import Logo from 'src/components/logo';
import { useLocales } from 'src/locales';
import { GuestChannelInfo } from 'src/requests/gathering';
import { LobbySocketProvider } from 'src/socket/LobbySocketProvider';
import randomUUID from 'src/utils/uuidv4';
import QuadriculadoTexture from '/assets/background/quadriculado-background-texture.png';
import { JoinConference } from '../../conference/JoinConference';
import { useGatheringRoomInfoQuery } from './useGatheringRoomInfoQuery';

export const GuestGatheringLobby = () => {
  const [loading, setLoading] = useState(false);
  const { translate } = useLocales();
  const { user: loggedUser, logoutGuest, refresh } = useAuthContext();

  const { externalLink = '' } = useParams();
  const { gathering } = useGatheringRoomInfoQuery({ externalLink });
  const deviceId = useRef<string>(randomUUID());

  const gatheringInfo = useMemo(
    () => ({
      ...gathering,
      externalLink,
      deviceId: deviceId.current,
    }),
    [externalLink, gathering],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    sessionStorage.removeItem('accessToken');
    if (loggedUser?.isGuest) {
      setLoading(true);
      logoutGuest();
      refresh().then(() => setLoading(false));
    }
    sessionStorage.setItem('externalLink', externalLink);
  }, []);

  return (
    <>
      <Helmet>
        <title> {translate('communicate.conference.helmet')} | Copera.ai</title>
      </Helmet>

      <LobbySocketProvider deviceId={deviceId.current}>
        <StyledRoot>
          <StyledSection>
            <CustomLogoContainer>
              <Logo disabledLink implicitTheme="dark" />
              <Typography variant="h3">copera.ai</Typography>
            </CustomLogoContainer>
            {loading || !gatheringInfo ? (
              <Stack
                justifyContent="center"
                alignItems="center"
                alignSelf="center"
                maxWidth={0.8}
                width={520}
              >
                <Loading />
              </Stack>
            ) : (
              <JoinConference gathering={gatheringInfo as GuestChannelInfo} />
            )}
            <Copyright variant="caption">
              © {new Date().getFullYear()} Copera.ai.{' '}
              {translate('login.all_rights_reserved')}
            </Copyright>
          </StyledSection>
        </StyledRoot>
      </LobbySocketProvider>
    </>
  );
};

const StyledRoot = styled('main')(() => ({
  height: '100%',
  width: ' 100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  background: '#000',

  backgroundImage: ` url(${QuadriculadoTexture})`,
  backgroundPosition: 'top',
  backgroundRepeat: 'no-repeat',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-10%',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '70%',
    height: '20%',
    background: 'rgba(47,85,234,0.5)',
    filter: 'blur(100px)',
    borderRadius: '50%',
    zIndex: 1,
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-15%',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '30%',
    height: '20%',
    background: 'rgba(100,73,204,0.5)',
    filter: 'blur(100px)',
    borderRadius: '50%',
    zIndex: 1,
  },
}));

const StyledSection = styled('div')(() => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  width: '100%',
  height: '100%',
  overflow: 'auto',
}));

const Copyright = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(8, 4, 4),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const CustomLogoContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(3),
  gap: theme.spacing(1.5),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(5),
  },
}));
